import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { formatApiMessage } from '@/api/client';
import { documentsApi } from '@/api/documents.api';
import { envelopesApi } from '@/api/envelopes.api';
import { templatesApi } from '@/api/templates.api';
import { SequentialChain } from '@/components/envelope/SequentialChain';
import { AppShell } from '@/components/layout/AppShell';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { formatAuditAction, formatBytes, formatDateTime } from '@/lib/format';
import { useAuthStore } from '@/stores/auth.store';

export function EnvelopeDetailPage() {
  const { t } = useTranslation();
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const me = useAuthStore((s) => s.user);

  const { data: envelope, isLoading, isError } = useQuery({
    queryKey: ['envelopes', 'detail', uuid],
    queryFn: () => envelopesApi.get(uuid!),
    enabled: !!uuid,
  });

  // Management actions (void/delete/remind/template/revision) are reserved
  // for the envelope's sender and org-level admins. Recipients (even when
  // they appear in the recipient list) can read but not destroy.
  const isPrivileged = me?.role === 'owner' || me?.role === 'admin';
  const isCreator = !!envelope && !!me && envelope.creator.id === me.id;
  const canManage = isPrivileged || isCreator;

  const { data: auditLogs = [] } = useQuery({
    queryKey: ['envelopes', 'audit', uuid],
    queryFn: () => envelopesApi.listAudit(uuid!),
    enabled: !!uuid && !!envelope && envelope.status !== 'draft',
  });

  const [activeDocId, setActiveDocId] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [showNewRevision, setShowNewRevision] = useState(false);
  const lastUrlRef = useRef<string | null>(null);

  // Auto-select the first document.
  useEffect(() => {
    if (envelope && envelope.documents.length > 0 && !activeDocId) {
      setActiveDocId(envelope.documents[0].id);
    }
  }, [envelope, activeDocId]);

  // Fetch PDF as blob URL whenever active document changes.
  // For completed envelopes we prefer the signed (flattened) PDF so the
  // preview shows the actual signatures, not the unsigned original.
  useEffect(() => {
    if (!activeDocId) return;
    let cancelled = false;
    const useSigned = envelope?.status === 'completed';
    const fetcher = useSigned
      ? documentsApi.fetchSignedAsBlobUrl(activeDocId)
      : documentsApi.fetchAsBlobUrl(activeDocId);
    fetcher
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        if (lastUrlRef.current) URL.revokeObjectURL(lastUrlRef.current);
        lastUrlRef.current = url;
        setPdfUrl(url);
      })
      .catch((err) => {
        if (!cancelled) toast.error(formatApiMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [activeDocId, envelope?.status]);

  // Cleanup on unmount.
  useEffect(() => () => {
    if (lastUrlRef.current) URL.revokeObjectURL(lastUrlRef.current);
  }, []);

  const deleteDocMutation = useMutation({
    mutationFn: documentsApi.delete,
    onSuccess: () => {
      toast.success(t('envelope_detail.document_removed'));
      queryClient.invalidateQueries({ queryKey: ['envelopes', 'detail', uuid] });
      setActiveDocId(null);
      setPdfUrl(null);
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const deleteEnvMutation = useMutation({
    mutationFn: () => envelopesApi.delete(uuid!),
    onSuccess: () => {
      toast.success(t('envelope_detail.envelope_deleted'));
      navigate('/envelopes');
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const remindMutation = useMutation({
    mutationFn: () => envelopesApi.remind(uuid!),
    onSuccess: (data) => {
      toast.success(
        data.remindersSent > 0
          ? t('envelope_detail.reminder_sent_count', { count: data.remindersSent })
          : t('envelope_detail.no_pending_recipient'),
      );
      queryClient.invalidateQueries({ queryKey: ['envelopes', 'detail', uuid] });
      queryClient.invalidateQueries({ queryKey: ['envelopes', 'audit', uuid] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const voidMutation = useMutation({
    mutationFn: (reason: string) => envelopesApi.void(uuid!, reason),
    onSuccess: () => {
      toast.success(t('envelope_detail.envelope_voided'));
      queryClient.invalidateQueries({ queryKey: ['envelopes', 'detail', uuid] });
      queryClient.invalidateQueries({ queryKey: ['envelopes', 'audit', uuid] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const saveAsTemplateMutation = useMutation({
    mutationFn: () => templatesApi.fromEnvelope(uuid!, {}),
    onSuccess: (tpl) => {
      toast.success(t('envelope_detail.template_saved_named', { name: tpl.title }));
      queryClient.invalidateQueries({ queryKey: ['templates', 'list'] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const newRevisionMutation = useMutation({
    mutationFn: (payload: { changeDescription?: string }) =>
      envelopesApi.newRevision(uuid!, payload),
    onSuccess: (env) => {
      toast.success(t('envelope_detail.revision_created'));
      queryClient.invalidateQueries({ queryKey: ['envelopes', 'list'] });
      setShowNewRevision(false);
      // Drop the user straight into the wizard so they can edit/replace
      // documents before re-sending — landing on the read-only-feeling
      // detail page is what made the old flow feel broken.
      navigate(`/envelopes/new?continue=${env.id}`);
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const setReviewDateMutation = useMutation({
    mutationFn: (date: string | null) => envelopesApi.setReviewDate(uuid!, date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['envelopes', 'detail', uuid] });
      toast.success(t('registry.review_saved'));
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  if (isLoading) {
    return (
      <AppShell>
        <div className="text-center text-muted py-12">{t('envelope_detail.loading')}</div>
      </AppShell>
    );
  }
  if (isError || !envelope) {
    return (
      <AppShell>
        <div className="card text-center py-12">
          <div className="text-3xl mb-2">😕</div>
          <div className="font-medium text-gray-900">{t('envelope_detail.not_found')}</div>
          <button onClick={() => navigate('/envelopes')} className="btn btn-secondary mt-4">
            {t('envelope_detail.back_to_list')}
          </button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <button onClick={() => navigate('/envelopes')} className="text-sm text-muted hover:text-gray-900 mb-3">
        {t('envelope_detail.back_to_list')}
      </button>

      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-semibold text-gray-900">{envelope.title}</h1>
            <StatusBadge status={envelope.status} />
          </div>
          {envelope.documentNumber && (
            <div className="text-[12px] text-muted mt-1">
              <code className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-900 font-medium">
                {envelope.documentNumber}
              </code>
            </div>
          )}
          <div className="flex items-center gap-2 text-[12px] text-muted mt-2">
            <span className="font-medium text-gray-700">{t('registry.col_next_review')}:</span>
            {envelope.reviewDateSource === 'algebra' ? (
              <span className="inline-flex items-center gap-1">
                <span className="text-gray-900">
                  {envelope.nextReviewDate ? envelope.nextReviewDate.slice(0, 10) : '—'}
                </span>
                <span
                  title={t('registry.review_from_algebra') as string}
                  className="text-[9px] uppercase tracking-wide bg-primary/10 text-primary rounded px-1 py-0.5"
                >
                  Algebra
                </span>
              </span>
            ) : canManage ? (
              <input
                type="date"
                value={envelope.nextReviewDate ? envelope.nextReviewDate.slice(0, 10) : ''}
                disabled={setReviewDateMutation.isPending}
                onChange={(e) => setReviewDateMutation.mutate(e.target.value || null)}
                title={t('registry.review_edit_hint') as string}
                className="input !py-0.5 !px-1.5 text-[12px] w-[140px]"
              />
            ) : (
              <span className="text-gray-900">
                {envelope.nextReviewDate ? envelope.nextReviewDate.slice(0, 10) : '—'}
              </span>
            )}
          </div>
          {envelope.message && (
            <p className="text-muted text-sm mt-2 max-w-2xl">{envelope.message}</p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {(envelope.status === 'sent' || envelope.status === 'in_progress') && canManage && (
            <>
              <button
                onClick={() => {
                  if (confirm(t('envelope_detail.remind_confirm'))) {
                    remindMutation.mutate();
                  }
                }}
                className="btn btn-secondary"
                disabled={remindMutation.isPending}
              >
                {remindMutation.isPending ? t('common.sending') : t('envelope_detail.send_reminder')}
              </button>
              <button
                onClick={() => {
                  const reason = prompt(t('envelope_detail.void_reason_prompt')) ?? '';
                  if (confirm(t('envelope_detail.void_confirm'))) {
                    voidMutation.mutate(reason);
                  }
                }}
                className="btn btn-secondary text-danger"
                disabled={voidMutation.isPending}
              >
                {voidMutation.isPending ? t('envelope_detail.voiding') : t('envelope_detail.void_envelope')}
              </button>
            </>
          )}
          {envelope.status === 'completed' && (
            <>
              {canManage && (
                <button
                  onClick={() => setShowNewRevision(true)}
                  className="btn btn-secondary"
                  disabled={newRevisionMutation.isPending}
                  title={t('envelope_detail.new_revision_hint')}
                >
                  {newRevisionMutation.isPending
                    ? t('common.creating')
                    : `R${envelope.numberingRevision + 1} ${t('envelope_detail.new_revision_btn')}`}
                </button>
              )}
              <button
                onClick={() => {
                  envelopesApi
                    .downloadCertificate(envelope.id, `${envelope.title}__certificate.pdf`)
                    .catch((err) => toast.error(formatApiMessage(err)));
                }}
                className="btn btn-secondary"
                title={t('envelope_detail.certificate_hint')}
              >
                {t('envelope_detail.download_certificate')}
              </button>
              {activeDocId && (
                <button
                  onClick={() => {
                    const doc = envelope.documents.find((d) => d.id === activeDocId);
                    if (!doc) return;
                    documentsApi
                      .downloadSigned(doc.id, `${doc.originalName.replace(/\.pdf$/i, '')}__signed.pdf`)
                      .catch((err) => toast.error(formatApiMessage(err)));
                  }}
                  className="btn btn-primary"
                >
                  {t('envelope_detail.download_signed')}
                </button>
              )}
            </>
          )}
          {['draft', 'sent', 'in_progress', 'completed'].includes(envelope.status) && canManage && (
            <button
              onClick={() => saveAsTemplateMutation.mutate()}
              className="btn btn-secondary"
              disabled={
                saveAsTemplateMutation.isPending ||
                envelope.documents.length === 0 ||
                envelope.recipients.length === 0
              }
              title={t('envelope_detail.save_template_hint')}
            >
              {saveAsTemplateMutation.isPending ? t('common.saving') : t('envelope_detail.save_as_template')}
            </button>
          )}
          {envelope.status === 'draft' && canManage && (
            <>
              <button
                onClick={() => navigate(`/envelopes/new?continue=${envelope.id}`)}
                className="btn btn-primary"
              >
                {t('envelope_detail.edit_and_send')}
              </button>
              <button
                onClick={() => {
                  if (confirm(t('envelope_detail.delete_confirm'))) {
                    deleteEnvMutation.mutate();
                  }
                }}
                className="btn btn-secondary text-danger"
                disabled={deleteEnvMutation.isPending}
              >
                {t('envelope_detail.delete_draft')}
              </button>
            </>
          )}
        </div>
      </div>

      <SequentialProgressBanner envelope={envelope} />

      <RevisionHistory envelopeUuid={envelope.id} currentRevision={envelope.numberingRevision} />

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Document list */}
        <aside>
          <div className="card p-3">
            <div className="text-xs uppercase tracking-wide font-semibold text-muted mb-2">
              {t('envelope_detail.documents_section', { count: envelope.documents.length })}
            </div>
            {envelope.documents.length === 0 ? (
              <div className="text-sm text-muted py-4 text-center">{t('documents_step.no_documents')}</div>
            ) : (
              <div className="space-y-1">
                {envelope.documents.map((doc) => (
                  <button
                    key={doc.id}
                    onClick={() => setActiveDocId(doc.id)}
                    className={`w-full text-left px-2.5 py-2 rounded-md text-sm transition-colors ${
                      activeDocId === doc.id
                        ? 'bg-primary-50 text-primary'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="font-medium truncate">{doc.originalName}</div>
                    <div className="text-xs text-muted mt-0.5">
                      {doc.pagesCount} {t('documents_step.pages')} · {formatBytes(doc.fileSize)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="card p-3 mt-3">
            <div className="text-xs uppercase tracking-wide font-semibold text-muted mb-2">
              {t('envelope_detail.recipients_section')} ({envelope.recipients.length})
            </div>
            {envelope.recipients.length === 0 ? (
              <div className="text-sm text-muted py-3 text-center">
                {t('recipients_step.no_recipients_yet')}
              </div>
            ) : (
              <ul className="space-y-2">
                {envelope.recipients
                  .slice()
                  .sort((a, b) => a.signingOrder - b.signingOrder)
                  .map((r) => {
                    const initials = (r.name[0] ?? '?').toUpperCase();
                    return (
                      <li key={r.id} className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-[11px] font-semibold shrink-0">
                            {initials}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-medium text-gray-900 truncate">
                              {r.name}
                              {envelope.workflowType === 'sequential' && (
                                <span className="text-muted font-normal ml-1">#{r.signingOrder}</span>
                              )}
                            </div>
                            <div className="text-[11px] text-muted truncate">{r.email}</div>
                          </div>
                          <RecipientStatusDot status={r.status} />
                        </div>
                        {r.status === 'declined' && r.declineReason && (
                          <div className="ml-9 rounded-md bg-danger-bg border border-danger/20 px-2.5 py-1.5">
                            <div className="text-[10px] uppercase tracking-wide font-semibold text-danger mb-0.5">
                              {r.declineReason.startsWith('[Revision requested]')
                                ? t('envelope_detail.revision_reason_label')
                                : t('envelope_detail.decline_reason_label')}
                            </div>
                            <div className="text-[12px] text-gray-700 whitespace-pre-wrap break-words">
                              {r.declineReason.replace('[Revision requested] ', '')}
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
              </ul>
            )}
          </div>

          <div className="card p-3 mt-3">
            <div className="text-xs uppercase tracking-wide font-semibold text-muted mb-2">
              {t('envelope_detail.details_section')}
            </div>
            <dl className="text-sm space-y-1.5">
              <div className="flex justify-between">
                <dt className="text-muted">{t('envelope_detail.details_workflow')}</dt>
                <dd className="text-gray-900">{t(`envelope.workflow_${envelope.workflowType}`)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted">{t('envelope_detail.details_created')}</dt>
                <dd className="text-gray-900">{formatDateTime(envelope.createdAt)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted">{t('envelope_detail.details_created_by')}</dt>
                <dd className="text-gray-900 truncate ml-2">
                  {envelope.creator.firstName} {envelope.creator.lastName}
                </dd>
              </div>
            </dl>
          </div>
        </aside>

        {/* PDF preview */}
        <main>
          {!activeDocId ? (
            <div className="card text-center py-20 text-muted">
              {envelope.documents.length === 0
                ? t('documents_step.no_documents')
                : t('envelope_detail.select_document')}
            </div>
          ) : !pdfUrl ? (
            <div className="card text-center py-20 text-muted">{t('envelope_detail.loading_pdf')}</div>
          ) : (
            <div className="card p-0 overflow-hidden">
              <div className="px-4 py-2 border-b border-border flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  {envelope.documents.find((d) => d.id === activeDocId)?.originalName}
                </div>
                {envelope.status === 'draft' && (
                  <button
                    onClick={() => {
                      if (confirm(t('envelope_detail.remove_doc_confirm'))) {
                        deleteDocMutation.mutate(activeDocId);
                      }
                    }}
                    className="btn btn-ghost text-danger"
                    disabled={deleteDocMutation.isPending}
                  >
                    {t('envelope_detail.remove_doc')}
                  </button>
                )}
              </div>
              <iframe
                title={t('envelope_detail.loading_pdf')}
                src={pdfUrl}
                className="w-full"
                style={{ height: '720px', border: 0 }}
              />
            </div>
          )}
        </main>
      </div>

      {(envelope.reviewComments?.length ?? 0) > 0 && (
        <section className="card mt-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-900">
              {t('envelope_detail.review_notes', 'Reviewer notes on the document')}
            </h3>
            <span className="text-xs text-muted">{envelope.reviewComments?.length}</span>
          </div>
          <ul className="space-y-2">
            {envelope.reviewComments?.map((c) => (
              <li key={c.id} className="border border-border rounded-lg px-3 py-2 bg-gray-50/60">
                <div className="flex items-center justify-between gap-2 text-[11px] text-muted mb-1">
                  <span className="font-semibold text-gray-800">{c.author}</span>
                  <span>
                    {t('envelope_detail.review_note_page', 'Page')} {c.page} ·{' '}
                    {new Date(c.at).toLocaleString('en-GB')}
                  </span>
                </div>
                <div className="text-[13px] text-gray-900 whitespace-pre-wrap">{c.text}</div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {envelope.status !== 'draft' && (
        <section className="card mt-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-900">{t('envelope_detail.audit_trail')}</h3>
            <span className="text-xs text-muted">{t('envelope_detail.audit_count', { count: auditLogs.length })}</span>
          </div>
          {auditLogs.length === 0 ? (
            <div className="text-sm text-muted py-4 text-center">{t('envelope_detail.no_events_yet')}</div>
          ) : (
            <ol className="relative border-l border-border ml-2 space-y-3 py-1">
              {auditLogs.map((log) => (
                <li key={log.id} className="ml-4 relative">
                  <span className="absolute -left-[21px] top-1.5 w-3 h-3 rounded-full bg-primary" />
                  <div className="flex flex-wrap items-baseline gap-x-3">
                    <span className="text-[13px] font-medium text-gray-900">{formatAuditAction(log.action)}</span>
                    <code className="text-[10px] text-muted bg-gray-50 px-1 rounded">{log.action}</code>
                    <span className="text-[11px] text-muted">{formatDateTime(log.createdAt)}</span>
                  </div>
                  <div className="text-[12px] text-muted mt-0.5 flex flex-wrap gap-x-3">
                    {log.actorEmail && <span>{t('envelope_detail.audit_actor')}: {log.actorEmail}</span>}
                    {log.ipAddress && <span>IP: {log.ipAddress}</span>}
                  </div>
                  {(() => {
                    const m = log.metadata as { note?: unknown } | null;
                    const noteText = m && typeof m.note === 'string' ? m.note : null;
                    return noteText ? (
                      <div className="text-[12px] text-gray-800 mt-1 bg-primary-50 border border-primary/20 rounded px-2 py-1">
                        💬 {noteText}
                      </div>
                    ) : null;
                  })()}
                  {!!log.metadata && (
                    <details className="mt-1">
                      <summary className="text-[11px] text-muted cursor-pointer">{t('envelope_detail.audit_metadata')}</summary>
                      <pre className="text-[11px] bg-gray-50 p-2 rounded mt-1 overflow-x-auto">
                        {JSON.stringify(log.metadata, null, 2)}
                      </pre>
                    </details>
                  )}
                </li>
              ))}
            </ol>
          )}
        </section>
      )}
      {showNewRevision && (
        <NewRevisionModal
          nextRevision={envelope.numberingRevision + 1}
          pending={newRevisionMutation.isPending}
          onClose={() => setShowNewRevision(false)}
          onSubmit={(changeDescription) => newRevisionMutation.mutate({ changeDescription })}
        />
      )}
    </AppShell>
  );
}

/**
 * Modal that opens a new revision. Captures the "Dəyişikliklərin təsviri"
 * (description of changes) the customer asked for — appears in the
 * registry alongside the new revision so reviewers see WHY it was
 * superseded, separately from the message addressed to recipients.
 */
function NewRevisionModal({
  nextRevision,
  pending,
  onClose,
  onSubmit,
}: {
  nextRevision: number;
  pending: boolean;
  onClose: () => void;
  onSubmit: (changeDescription: string) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  return (
    <div
      className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg border border-border w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-base font-semibold text-gray-900">
            R{nextRevision} — {t('revision.new_modal_title')}
          </h3>
          <p className="text-[12px] text-muted mt-1">
            {t('revision.new_modal_subtitle')}
          </p>
        </div>
        <div className="p-5">
          <label className="label">{t('revision.change_description')}</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="textarea min-h-[120px]"
            placeholder={t('revision.change_description_placeholder') as string}
            maxLength={2000}
          />
          <p className="text-[11px] text-muted mt-1">
            {t('revision.change_description_hint')}
          </p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border">
          <button onClick={onClose} className="btn btn-secondary" disabled={pending}>
            {t('common.cancel')}
          </button>
          <button
            onClick={() => onSubmit(text)}
            className="btn btn-primary"
            disabled={pending}
          >
            {pending ? t('common.creating') : t('revision.new_modal_submit')}
          </button>
        </div>
      </div>
    </div>
  );
}

function RecipientStatusDot({
  status,
}: {
  status: 'pending' | 'sent' | 'viewed' | 'signed' | 'declined' | 'expired';
}) {
  const { t } = useTranslation();
  const map = {
    pending:  'bg-gray-300',
    sent:     'bg-info',
    viewed:   'bg-warning',
    signed:   'bg-success',
    declined: 'bg-danger',
    expired:  'bg-gray-400',
  } as const;
  return (
    <span
      title={t(`envelope.recipient_status_${status}`)}
      className={`w-2 h-2 rounded-full shrink-0 ${map[status]}`}
    />
  );
}

/**
 * Sequential workflow visibility — surfaces "Currently waiting for: X (#N)"
 * at the top of the detail page so the sender doesn't have to scan the
 * recipient list to figure out who's blocking the envelope.
 *
 * Renders nothing if the envelope is parallel, in draft/completed/declined,
 * or has no recipients with a pending step.
 */
function SequentialProgressBanner({ envelope }: { envelope: import('@/api/envelopes.api').EnvelopeView }) {
  if (envelope.status === 'draft') return null;
  return (
    <div className="mb-5">
      <SequentialChain
        recipients={envelope.recipients}
        workflowType={envelope.workflowType}
        envelopeStatus={envelope.status}
      />
    </div>
  );
}

/**
 * Shows the full R1→R2→… lineage when the envelope is part of a revision
 * chain. Hidden when this is a standalone (R1, no children) envelope so we
 * don't bloat the page with single-row noise.
 */
function RevisionHistory({
  envelopeUuid,
  currentRevision,
}: {
  envelopeUuid: string;
  currentRevision: number;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: nodes } = useQuery({
    queryKey: ['envelopes', 'lineage', envelopeUuid],
    queryFn: () => envelopesApi.lineage(envelopeUuid),
  });

  if (!nodes || nodes.length <= 1) return null;
  // Only show lineage when this envelope is actually revised (R>=2) or has
  // descendants. A solo R1 with no children would already be hidden by the
  // length<=1 check above, but be explicit.
  if (nodes.length === 1 && currentRevision === 1) return null;

  // Detect supersession — if the current envelope has any later revision,
  // the user is looking at a stale version and should be nudged.
  const currentIdx = nodes.findIndex((n) => n.isCurrent);
  const latest = nodes[nodes.length - 1];
  const isSuperseded = currentIdx >= 0 && currentIdx < nodes.length - 1 && latest;

  return (
    <div className="card !p-0 mb-5">
      {isSuperseded && (
        <div className="px-4 py-2.5 bg-warning-bg border-b border-warning flex items-center gap-2 flex-wrap">
          <span className="text-warning-fg text-[13px]">⚠</span>
          <span className="text-[12px] text-warning-fg flex-1">
            {t('revision.superseded_notice', { revision: latest.revision })}
          </span>
          <button
            onClick={() => navigate(`/envelopes/${latest.id}`)}
            className="btn btn-sm btn-primary"
          >
            {t('revision.open_latest', { revision: latest.revision })}
          </button>
        </div>
      )}
      <div className="px-4 py-3 border-b border-border bg-gray-50">
        <h3 className="text-[13px] font-semibold text-gray-900 m-0">
          {t('revision.history_title')}
        </h3>
        <p className="text-[11px] text-muted mt-0.5">
          {t('revision.history_subtitle', { count: nodes.length })}
        </p>
      </div>
      <div className="px-4 py-4 flex items-center gap-0 overflow-x-auto">
        {nodes.map((node, i) => (
          <div key={node.id} className="flex items-center">
            <button
              onClick={() => !node.isCurrent && navigate(`/envelopes/${node.id}`)}
              disabled={node.isCurrent}
              className={`flex flex-col items-start text-left px-3 py-2 rounded-md min-w-[180px] border transition-colors ${
                node.isCurrent
                  ? 'border-primary bg-primary-50 cursor-default'
                  : 'border-border hover:bg-gray-50 cursor-pointer'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span
                  className={`text-[11px] font-bold rounded px-1.5 py-0.5 ${
                    node.isCurrent ? 'bg-primary text-white' : 'bg-gray-200 text-gray-800'
                  }`}
                >
                  R{node.revision}
                </span>
                <RevisionStatusPill status={node.status} />
              </div>
              {node.documentNumber && (
                <code className="text-[10px] text-gray-700 bg-gray-100 px-1 rounded">
                  {node.documentNumber}
                </code>
              )}
              <div className="text-[10px] text-muted mt-1">
                {node.creator.firstName} {node.creator.lastName}
              </div>
              <div className="text-[10px] text-muted">
                {new Date(node.completedAt ?? node.createdAt).toLocaleDateString()}
              </div>
            </button>
            {i < nodes.length - 1 && (
              <div className="w-6 h-0.5 bg-gray-300 mx-1" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function RevisionStatusPill({ status }: { status: string }) {
  const { t } = useTranslation();
  const cls =
    status === 'completed'
      ? 'bg-success/10 text-success'
      : status === 'declined' || status === 'voided' || status === 'expired'
      ? 'bg-danger/10 text-danger'
      : status === 'draft'
      ? 'bg-gray-100 text-gray-700'
      : 'bg-warning-bg text-warning-fg';
  return (
    <span className={`text-[9px] uppercase tracking-wide rounded px-1.5 py-0.5 ${cls}`}>
      {t(`envelope.status_${status}`, { defaultValue: status })}
    </span>
  );
}
